import {
  CatalogItem,
  type CatalogItemCategory,
  type Requisition,
  ToolFundSettings,
  User,
} from '@kalos/kalos-rpc';
import { toast } from '@kalos/ui';
import { useMemo } from 'react';

import { useAuth } from '../../../context/AuthContext';
import { useBatchGetAssetInstanceQuery } from '../../../hooks/react-query/useAssetInstanceQuery';
import { useEmailClientSendMutation } from '../../../hooks/react-query/useEmailClientQuery';
import { useToolFundCatalogItemCategoryQuery } from '../../../hooks/react-query/useToolFundCatalogItemCategoryQuery';
import { useToolFundCatalogQuery } from '../../../hooks/react-query/useToolFundCatalogQuery';
import { useRequisitionCreateMutation } from '../../../hooks/react-query/useToolFundQuery';
import {
  useGetToolFundSettings,
  useUserToolFundBalanceAvailableQuery,
} from '../../../hooks/react-query/useToolFundSettingsQuery';
import { useToolSetsQuery } from '../../../hooks/react-query/useToolSetQuery';
import { useUserQuery } from '../../../hooks/react-query/useUserQuery';
import { useLoggedInUserManagedDepartments } from '../../../hooks/useLoggedInUserManagedDepartments';
import {
  exceedsToolFundBalance,
  filterToolsByCategory,
  type RequisitionTool,
  requisitionTotalCost,
  toCatalogItemOptions,
  toolFundNewBalance,
} from './requisitionShared';

/** Managers request for others, use tool sets and skip the trade filter and the received check. */
export const useIsRequisitionManager = () => {
  const { user } = useAuth();
  return useMemo(() => {
    return user.permissionGroups.some((group) => group.name === 'Manager');
  }, [user.permissionGroups]);
};

/** ToolFundAdmins may charge a request to the person it's for instead of themselves. */
export const useIsToolFundAdmin = () => {
  const { user } = useAuth();
  return useMemo(
    () => user.permissionGroups.some((group) => group.name === 'ToolFundAdmin'),
    [user.permissionGroups],
  );
};

/**
 * The tools a person may request: their trade's tools plus those for every trade (trade '0'), or
 * the whole catalog when `ignoreTradeRestriction` (managers). Moved from `RequisitionForm`
 * unchanged: the same queries, keys and enabled flags.
 */
export const useRequisitionCatalog = ({
  userId,
  ignoreTradeRestriction,
  selectedCategoryId,
}: {
  userId: number;
  ignoreTradeRestriction: boolean;
  /** Desktop narrows its tool list by the first line's category; the phone passes none. */
  selectedCategoryId: number | undefined;
}) => {
  const { data: userSettings } = useGetToolFundSettings(ToolFundSettings.create({ userId }));

  const { data: categories } = useToolFundCatalogItemCategoryQuery();

  const catalogQuery = useToolFundCatalogQuery({
    filter: CatalogItem.create({
      pageNumber: -1,
      ...(ignoreTradeRestriction ? {} : { tradeTypeIdList: userSettings?.tradeTypeId?.toString() }),
    }),
  });

  // Get items with trade id 0 (for all trades) when there's a trade restriction
  const catalogQueryForAllTrades = useToolFundCatalogQuery({
    filter: CatalogItem.create({
      pageNumber: -1,
      tradeTypeIdList: '0',
    }),
    enabled: !ignoreTradeRestriction && !!userSettings?.tradeTypeId,
  });

  // Get tools filtered by trade
  const filteredTools = useMemo(() => {
    if (!catalogQuery.data?.results) return [];

    const tradeSpecificTools = catalogQuery.data.results;

    if (!ignoreTradeRestriction && catalogQueryForAllTrades.data?.results) {
      const allTradesTools = catalogQueryForAllTrades.data.results;
      const combinedTools = [...tradeSpecificTools, ...allTradesTools];
      const uniqueTools = combinedTools.filter(
        (tool, index, self) => self.findIndex((t) => t.id === tool.id) === index,
      );

      return uniqueTools;
    }

    return tradeSpecificTools;
  }, [catalogQuery.data?.results, catalogQueryForAllTrades.data?.results, ignoreTradeRestriction]);

  const catalogItems = useMemo(() => {
    if (!filteredTools.length) return [];

    // Show all tools if no category is selected, or filter by category if one is selected
    return toCatalogItemOptions(filterToolsByCategory(filteredTools, selectedCategoryId));
  }, [filteredTools, selectedCategoryId]);

  const isCatalogItemsQueryPending =
    catalogQuery.isPending || (!ignoreTradeRestriction && catalogQueryForAllTrades.isPending);

  const categoryOptions = useMemo(() => {
    if (!categories?.results) return [];

    if (ignoreTradeRestriction) {
      return categories.results.map((category: CatalogItemCategory) => ({
        label: category.name,
        value: category.id.toString(),
      }));
    }

    const categoryIdsWithTools = new Set(filteredTools.map((tool) => tool.categoryId));

    return categories.results
      .filter((category) => categoryIdsWithTools.has(category.id))
      .map((category: CatalogItemCategory) => ({
        label: category.name,
        value: category.id.toString(),
      }));
  }, [categories?.results, ignoreTradeRestriction, filteredTools]);

  return {
    userSettings,
    categories,
    filteredTools,
    catalogItems,
    categoryOptions,
    isCatalogItemsQueryPending,
    isCatalogError: catalogQuery.isError || catalogQueryForAllTrades.isError,
    isCatalogFetching: catalogQuery.isFetching || catalogQueryForAllTrades.isFetching,
    refetchCatalog: () => {
      void catalogQuery.refetch();
      if (catalogQueryForAllTrades.isError) void catalogQueryForAllTrades.refetch();
    },
  };
};

/** The tool sets a manager may add, from the departments they manage. Moved from desktop. */
export const useRequisitionToolSets = (selectedDepartmentId: number | undefined) => {
  // Get departments that the logged-in user has access to
  const managedDepartments = useLoggedInUserManagedDepartments();

  const toolSetsQuery = useToolSetsQuery({
    departmentId: selectedDepartmentId,
    pageNumber: -1, // Get all tool sets
  });

  const departmentOptions = useMemo(() => {
    const options = [
      { label: 'All departments', value: 'all' },
      ...(managedDepartments?.map((dept) => ({
        label: dept.description || '',
        value: dept.id.toString(),
      })) ?? []),
    ];
    return options;
  }, [managedDepartments]);

  const toolSetOptions = useMemo(() => {
    const toolSetsList = toolSetsQuery.data?.results || [];
    const managedDepartmentIds = managedDepartments?.map((dept) => dept.id) || [];

    // Filter tool sets to only include those from departments the user has access to
    const filteredToolSets = toolSetsList.filter((toolSet) => {
      // If no department filter is selected, only show tool sets from managed departments
      if (!selectedDepartmentId) {
        return managedDepartmentIds.includes(toolSet.departmentId);
      }
      // If a specific department is selected, show tool sets from that department (if user has access)
      return (
        toolSet.departmentId === selectedDepartmentId &&
        managedDepartmentIds.includes(toolSet.departmentId)
      );
    });

    return filteredToolSets.map((toolSet) => ({
      label: toolSet.name,
      value: toolSet.id.toString(),
    }));
  }, [toolSetsQuery.data?.results, managedDepartments, selectedDepartmentId]);

  return { toolSetsQuery, departmentOptions, toolSetOptions };
};

/** The balance check, against whoever the request is charged to (`userId`). */
export const useRequisitionBalance = ({
  userId,
  tools,
  catalogItems,
}: {
  userId: number;
  tools: RequisitionTool[];
  catalogItems: ReturnType<typeof toCatalogItemOptions>;
}) => {
  const balanceQuery = useUserToolFundBalanceAvailableQuery(User.create({ id: userId }));
  const availableBalance = balanceQuery.data?.availableBalance ?? 0;
  const totalCost = useMemo(() => requisitionTotalCost(tools, catalogItems), [tools, catalogItems]);
  const exceedsBalance = useMemo(
    () => exceedsToolFundBalance(availableBalance, totalCost),
    [availableBalance, totalCost],
  );
  const newBalance = useMemo(
    () => toolFundNewBalance(availableBalance, totalCost),
    [availableBalance, totalCost],
  );
  return { balanceQuery, availableBalance, totalCost, exceedsBalance, newBalance };
};

/**
 * Whether the person has approved tools they haven't marked received. Desktop's query, moved
 * from `RequisitionFormDialog` unchanged.
 */
export const useUnreceivedToolsQuery = (userId: number) =>
  useBatchGetAssetInstanceQuery({
    filters: {
      currentUserId: userId,
      acquisitionDate: '',
      fieldMask: ['AcquisitionDate'],
    },
    select(data) {
      return { doesUserHavePendingReceiveItems: data.totalCount > 0 };
    },
  });

/**
 * Sends the requisition, then the "Tool Fund Request Submitted" email to `userId`'s address (the
 * log's owner, not a technician a manager picked: ClickUp 86bcdca31). Moved from
 * `RequisitionFormDialog` unchanged; `onCreated` runs where desktop closed its dialog.
 */
export const useSubmitRequisition = ({
  userId,
  onCreated,
  onCreateError,
}: {
  userId: number;
  onCreated: () => void;
  /** Desktop only logs a failed create; the phone also says so. */
  onCreateError?: () => void;
}) => {
  const createRequisition = useRequisitionCreateMutation();
  const emailClientSend = useEmailClientSendMutation();

  const userQuery = useUserQuery({
    filters: { id: userId },
  });

  const submit = async (data: Requisition) => {
    try {
      const createdRequisition = await createRequisition.mutateAsync(data);
      onCreated();

      // Send email notification
      try {
        if (userQuery.isSuccess && userQuery.data?.email) {
          const totalCost =
            data.items?.reduce((total, item) => {
              return total + (item.estimatedCost || 0);
            }, 0) || 0;

          const toolsList =
            data.items?.map((item) => `<li>${item.customItemName || 'Tool'}</li>`).join('') || '';

          const emailTemplate = `
            <div style="font-family: Arial, sans-serif; padding: 20px;">
              <h2>Tool Fund Request Submitted</h2>
              <p>Your tool fund request has been successfully submitted and is pending approval.</p>
              <div style="margin: 20px 0; padding: 15px; background-color: #f8f9fa; border-radius: 4px;">
                <h3 style="margin-top: 0;">Request Details:</h3>
                <ul>
                  <li>Request ID: ${createdRequisition.id}</li>
                  <li>Status: Pending</li>
                  <li>Estimated Total Cost: $${totalCost.toFixed(2)}</li>
                </ul>
                <h4>Tools Requested:</h4>
                <ul>
                  ${toolsList}
                </ul>
              </div>
              <p>You will receive another email once your request has been reviewed.</p>
            </div>
          `;

          await emailClientSend.mutateAsync({
            subject: 'Tool Fund Request Submitted',
            body: emailTemplate,
            recipient: userQuery.data.email,
          });
        }
      } catch (emailError) {
        console.error('Failed to send notification email:', emailError);
        toast({
          variant: 'destructive',
          title: 'Request created but email failed',
          description:
            'Your request was created successfully, but we could not send the notification email.',
        });
      }
    } catch (error) {
      console.error('Error submitting requisition:', error);
      onCreateError?.();
    }
  };

  return { submit, isSubmitting: createRequisition.isPending };
};
